import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangleIcon, CheckIcon, PencilLineIcon, ShieldAlertIcon, UtensilsCrossedIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card, EmptyState } from '../../components/ui/Panels';
import { Button, ButtonLink } from '../../components/ui/Button';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { Badge } from '../../components/ui/Badge';
import { chefById } from '../../data/chefs';
import { customer, allergySummary, severityLabel, severityTone } from '../../data/customer';
import { occasions, equipmentLabel } from '../../data/taxonomy';
import { useAppData } from '../../contexts/AppDataContext';
import { useLocale } from '../../contexts/LocaleContext';
import { currency } from '../../utils/format';
import { GROCERY_LABEL, SERVICE_FEE_RATE, TAX_RATE, missingEquipment, priceQuote } from '../../utils/pricing';
import type { Fulfillment } from '../../types/chef';
export function BookingRequest() {
  const {
    slug
  } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const {
    createRequest
  } = useAppData();
  const {
    locale,
    currencyCode
  } = useLocale();
  const chef = chefById(slug ?? '');
  const [occasion, setOccasion] = useState(occasions[0]);
  const [date, setDate] = useState('2026-09-19');
  const [time, setTime] = useState('19:00');
  const [guests, setGuests] = useState(6);
  const [menuId, setMenuId] = useState(params.get('menu') ?? chef?.menus[0]?.id ?? '');
  const [customBrief, setCustomBrief] = useState('');
  const [budgetPerGuest, setBudgetPerGuest] = useState(1500);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const custom = menuId === 'custom';
  const selectedMenu = chef?.menus.find(m => m.id === menuId);
  const menu = selectedMenu ?? chef?.menus[0];
  const fulfillment: Fulfillment = !custom && menu && !menu.fulfillment.includes('in_home') ? 'delivery' : 'in_home';
  const estimate = useMemo(() => {
    if (!chef || !menu) return null;
    if (custom) {
      const subtotal = Math.max(0, budgetPerGuest * guests);
      const serviceFee = Math.round(subtotal * SERVICE_FEE_RATE);
      const tax = Math.round((subtotal + serviceFee) * TAX_RATE);
      const total = subtotal + serviceFee + tax;
      return {
        subtotal,
        serviceFee,
        tax,
        total,
        depositDue: Math.round(total * chef.depositPercent / 100),
        minimumShortfall: Math.max(0, chef.minimumSpend - subtotal)
      };
    }
    const priced = priceQuote(chef, {
      menuId: menu.id,
      fulfillment,
      guests: Math.max(guests, menu.minGuests),
      date,
      time,
      hours: menu.includedHours,
      addOns: [],
      travelKm: 9,
      note: '',
      allergiesAcknowledged: false
    });
    return {
      subtotal: priced.subtotal,
      serviceFee: priced.serviceFee,
      tax: priced.tax,
      total: priced.total,
      depositDue: priced.depositDue,
      minimumShortfall: priced.minimumShortfall
    };
  }, [chef, menu, custom, budgetPerGuest, guests, date, time, fulfillment]);
  if (!chef || !menu) {
    return <AppShell title="Request a quote" data-id="element-1703" data-mp="1mmzwxt7yqft3">
        <EmptyState icon={<UtensilsCrossedIcon className="h-5 w-5" aria-hidden="true" data-id="element-1705" data-mp="1mkndfbmk3xdx" />} title="We couldn’t find that chef" body="The listing may have been paused while you were browsing." action={<ButtonLink to="/discover" data-id="element-1706" data-mp="18mp3qjj6kp4r">Back to search</ButtonLink>} data-id="element-1704" data-mp="1mm7z0bqw85zn" />
      </AppShell>;
  }
  const gaps = !custom && fulfillment === 'in_home' ? missingEquipment(menu, customer.kitchen.equipment) : [];
  const belowMinGuests = !custom && guests < menu.minGuests;
  const minOrder = fulfillment === 'delivery' ? chef.minDeliveryOrder : chef.minimumSpend;
  const belowMinOrder = !!estimate && estimate.subtotal < minOrder;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (belowMinGuests) {
      setError(`${menu.name} needs at least ${menu.minGuests} guests.`);
      return;
    }
    if (custom && customBrief.trim().length < 20) {
      setError('Tell the chef what you have in mind — a couple of sentences is enough.');
      return;
    }
    if (belowMinOrder) {
      setError(`${chef.name.split(' ')[0]} takes bookings from ${currency(minOrder, locale, currencyCode)} upwards. Add guests, raise the budget, or pick a fuller menu.`);
      return;
    }
    setError(null);
    const brief = custom ? `Own menu proposal (budget ${currency(budgetPerGuest, locale, currencyCode)} per guest): ${customBrief}` : '';
    const {
      threadId
    } = createRequest({
      chefId: chef.id,
      occasion,
      date,
      time,
      guests,
      fulfillment,
      menuId: custom ? undefined : menu.id,
      note: [brief, note].filter(Boolean).join('\n\n')
    });
    navigate(`/app/inbox/${threadId}`);
  };
  return <AppShell title={`Request a quote from ${chef.name.split(' ')[0]}`} subtitle="No payment yet — this opens a conversation." data-id="element-1707" data-mp="egrhdnstgeea">
      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1708" data-mp="ocl3nywc7m99">
        <div className="space-y-6" data-id="element-1709" data-mp="jdrp035bdp7i">
          <Card className="p-5" data-id="element-1710" data-mp="jcq7pgxpg6hc">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1711" data-mp="jfumf88sqkm9">The occasion</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-1712" data-mp="xbpz2wclqzh7">
              <Field label="What is it for" id="r-occasion" data-id="element-1713" data-mp="sf7vw0wx796z">
                <Select id="r-occasion" value={occasion} onChange={e => setOccasion(e.target.value)} data-id="element-1714" data-mp="sclny064aro8">
                  {occasions.map(o => <option key={o} value={o} data-id="element-1715" data-mp="125aow141kn5b">
                      {o}
                    </option>)}
                </Select>
              </Field>
              <Field label="Guests" id="r-guests" error={belowMinGuests ? `Minimum ${menu.minGuests} for this menu` : undefined} data-id="element-1716" data-mp="1c2p5c0pytbxm">
                <Input id="r-guests" type="number" min={1} max={40} value={guests} onChange={e => setGuests(Number(e.target.value) || 1)} className="tnum" data-id="element-1717" data-mp="1j6okqyqa73ax" />
              </Field>
              <Field label="Date" id="r-date" hint={`${menu.leadTimeDays} days notice`} data-id="element-1718" data-mp="1xvui0h5k6jg6">
                <Input id="r-date" type="date" value={date} onChange={e => setDate(e.target.value)} data-id="element-1719" data-mp="21vlgl71bu3v9" />
              </Field>
              <Field label="Start time" id="r-time" data-id="element-1720" data-mp="26xaseowdilke">
                <Input id="r-time" type="time" value={time} onChange={e => setTime(e.target.value)} data-id="element-1721" data-mp="2bvcb38pw0ivc" />
              </Field>
            </div>
          </Card>

          <Card className="p-5" data-id="element-1722" data-mp="hg9qnpfp3qza">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1723" data-mp="hh1ol6wrm0sq">Which menu are you thinking of?</h2>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1724" data-mp="mge7jgkimnq1">Pick one of {chef.name.split(' ')[0]}’s menus, or describe your own.</p>
            <div className="mt-4 space-y-2.5" data-id="element-1725" data-mp="2zqxiegwkzlye">
              {chef.menus.map(option => {
              const active = option.id === menuId;
              const optionPrice = option.fulfillment.includes('in_home') ? option.pricePerGuest : option.pricePerPortion ?? option.pricePerGuest;
              return <label key={option.id} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition-colors duration-150 ease-out ${active ? 'border-primary bg-primary-soft' : 'border-hairline hover:border-primary/40'}`} data-id="element-1726" data-mp="3ip0a53gijwzw">
                    <input type="radio" name="menu" value={option.id} checked={active} onChange={() => setMenuId(option.id)} className="mt-1 accent-primary" data-id="element-1727" data-mp="34qtnkjh0ljfj" />
                    <span className="min-w-0 flex-1" data-id="element-1728" data-mp="3te67quzxysq2">
                      <span className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-1729" data-mp="26jzsq66w43e">
                        <span className="font-medium text-ink" data-id="element-1730" data-mp="3jep1ocgs2ctk">{option.name}</span>
                        <span className="tnum text-sm font-medium text-ink" data-id="element-1731" data-mp="3oc75jofxu1up">
                          {currency(optionPrice, locale, currencyCode)} /{' '}
                          {option.fulfillment.includes('in_home') ? 'guest' : 'portion'}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-[13px] text-ink-2" data-id="element-1732" data-mp="67vqkz0suvd6">{option.tagline}</span>
                      <span className="mt-1 block text-[12px] text-ink-3" data-id="element-1733" data-mp="b5f4fxtl564l">
                        min {option.minGuests} · {GROCERY_LABEL[option.groceries]} ·{' '}
                        {option.fulfillment.includes('in_home') ? `${option.includedHours} hrs included` : 'delivered'}
                      </span>
                    </span>
                  </label>;
            })}

              <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition-colors duration-150 ease-out ${custom ? 'border-primary bg-primary-soft' : 'border-dashed border-hairline hover:border-primary/40'}`} data-id="element-1734" data-mp="2jelm16e1kpmi">
                <input type="radio" name="menu" value="custom" checked={custom} onChange={() => setMenuId('custom')} className="mt-1 accent-primary" data-id="element-1735" data-mp="25f3s6clw2m6a" />
                <span className="min-w-0 flex-1" data-id="element-1736" data-mp="2addqgw2wu2w4">
                  <span className="flex items-center gap-2 font-medium text-ink" data-id="element-1737" data-mp="2abu6aue4v7p2">
                    <PencilLineIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-1738" data-mp="3pskug84v6suh" />
                    Propose my own menu
                  </span>
                  <span className="mt-0.5 block text-[13px] text-ink-2" data-id="element-1739" data-mp="3kml01zxjgr3m">
                    Describe the food and set a budget per guest. The chef prices it against what you have asked for.
                  </span>
                </span>
              </label>
            </div>

            {custom && <div className="mt-4 grid gap-4 rounded-lg border border-hairline bg-oat p-4 sm:grid-cols-[minmax(0,1fr)_180px]" data-id="element-1740" data-mp="36vfxoq2sdkpl">
                <Field label="What would you like cooked?" id="r-brief" hint="Dishes, style, how spicy, anything fixed" error={custom && error && customBrief.trim().length < 20 ? 'A little more detail, please' : undefined} data-id="element-1741" data-mp="31x61qd98mg2x">
                  <Textarea id="r-brief" rows={5} value={customBrief} onChange={e => setCustomBrief(e.target.value)} placeholder="A southern Thai seafood table for eight — gaeng som, grilled prawns, one whole fish. Medium heat for two guests, proper heat for the rest. No shellfish at all for me." data-id="element-1742" data-mp="2nxn4ifhysp5e" />
                </Field>
                <div data-id="element-1743" data-mp="13eb0xrtkd09b">
                  <Field label="Budget per guest" id="r-budget" hint="Baht, food only" data-id="element-1744" data-mp="13drkygnh4lxx">
                    <Input id="r-budget" type="number" min={200} step={100} value={budgetPerGuest} onChange={e => setBudgetPerGuest(Number(e.target.value) || 0)} className="tnum" data-id="element-1745" data-mp="18d4fzooi0zbl" />
                  </Field>
                  <p className="mt-2 text-[12px] text-ink-3" data-id="element-1746" data-mp="opew03mt03cx">
                    That is {currency(budgetPerGuest * guests, locale, currencyCode)} for {guests} guests, before fees.
                  </p>
                  <p className="mt-2 text-[12px] text-ink-3" data-id="element-1747" data-mp="204jyb6j0vzw0">
                    {chef.name.split(' ')[0]}’s menus run{' '}
                    {currency(Math.min(...chef.menus.map(m => m.pricePerGuest)), locale, currencyCode)}–
                    {currency(Math.max(...chef.menus.map(m => m.pricePerGuest)), locale, currencyCode)} per guest.
                  </p>
                </div>
              </div>}
          </Card>

          <Card className="p-5" data-id="element-1748" data-mp="52gmzj0ryk4n">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1749" data-mp="4zav5w0rh458">Anything else they should know?</h2>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1750" data-mp="3deulona1s8gl">Your allergy record is attached automatically — this is for everything else.</p>
            <Textarea className="mt-4" rows={4} value={note} onChange={e => setNote(e.target.value)} placeholder="It's my partner's birthday. Our daughter eats early and won't touch anything spicy. No charcoal allowed on the balcony." aria-label="Message to the chef" data-id="element-1751" data-mp="3n7jv7cm1fc9a" />
            {error && <p role="alert" className="mt-3 text-sm font-medium text-danger" data-id="element-1752" data-mp="159u03s8akltr">
                {error}
              </p>}
            <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1753" data-mp="1a13pcrpxhn54">
              <Button type="submit" variant="action" size="lg" data-id="element-1754" data-mp="w9fessvbv6q9">
                Send request
              </Button>
              <p className="text-[13px] text-ink-3" data-id="element-1755" data-mp="1191tz7upux1p">
                {chef.name.split(' ')[0]} usually replies within{' '}
                {chef.responseMinutes < 60 ? `${chef.responseMinutes} minutes` : `${Math.round(chef.responseMinutes / 60)} hours`}.
              </p>
            </div>
          </Card>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start" data-id="element-1756" data-mp="1eyvh9lqkp99b">
          <Card className="p-5" data-id="element-1757" data-mp="1jxowbilid8bl">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1758" data-mp="1jwn96fr7wvdi">
              {custom ? 'Your budget, with fees' : 'Rough estimate'}
            </h2>
            <p className="tnum mt-2 font-display text-3xl font-semibold tracking-[-0.02em] text-ink" data-id="element-1759" data-mp="8k4c35uf329h">
              {estimate ? currency(estimate.total, locale, currencyCode) : '—'}
            </p>
            <p className="mt-1 text-[13px] text-ink-2" data-id="element-1760" data-mp="j1g8fpppfz2x">
              {custom ? `${guests} guests · your own menu` : `${Math.max(guests, menu.minGuests)} guests · ${menu.name}`}
            </p>
            <dl className="mt-4 space-y-1.5 border-t border-hairline pt-3 text-[13px]" data-id="element-1761" data-mp="a2mey87o7otl">
              <div className="flex justify-between gap-3" data-id="element-1762" data-mp="16s3v5h4a4t9j">
                <dt className="text-ink-2" data-id="element-1763" data-mp="16phk37i6jvmk">{custom ? 'Food budget' : 'Menu'}</dt>
                <dd className="tnum text-ink" data-id="element-1764" data-mp="12o4g72yne2ia">{estimate ? currency(estimate.subtotal, locale, currencyCode) : '—'}</dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1765" data-mp="ssb3njkxi90i">
                <dt className="text-ink-2" data-id="element-1766" data-mp="su5h6ctveyuk">Service fee &amp; tax</dt>
                <dd className="tnum text-ink" data-id="element-1767" data-mp="3ii64ox94up3w">
                  {estimate ? currency(estimate.serviceFee + estimate.tax, locale, currencyCode) : '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1768" data-mp="33fegxyox57ki">
                <dt className="text-ink-2" data-id="element-1769" data-mp="38dfyugalz0ue">Deposit if you accept</dt>
                <dd className="tnum text-ink" data-id="element-1770" data-mp="38dpr6aj4lk39">{estimate ? currency(estimate.depositDue, locale, currencyCode) : '—'}</dd>
              </div>
            </dl>
            <p className="mt-3 text-[12px] text-ink-3" data-id="element-1771" data-mp="3i3hnb96vil9u">
              The chef’s quote is the real number — add-ons, travel and groceries can move it.
            </p>

            <div className={`mt-4 rounded-lg p-3 text-[13px] ${belowMinOrder ? 'bg-danger-soft text-danger' : 'bg-oat text-ink-2'}`} data-id="element-1772" data-mp="3m77c9zrgn4ve">
              <span className="font-medium" data-id="element-1773" data-mp="3w16a4mki42hj">
                Minimum {fulfillment === 'delivery' ? 'delivery order' : 'booking'}: {currency(minOrder, locale, currencyCode)}
              </span>
              {belowMinOrder && estimate ? <span className="mt-1 block" data-id="element-1774" data-mp="26nax318ji7rm">
                  You are {currency(minOrder - estimate.subtotal, locale, currencyCode)} short — this request cannot be sent yet.
                </span> : <span className="mt-1 block text-ink-3" data-id="element-1775" data-mp="1tplft69cnngr">Set by the chef. Your request clears it.</span>}
            </div>
          </Card>

          <Card className="p-5" data-id="element-1776" data-mp="23mhkf0w4npg1">
            <h2 className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1777" data-mp="23lg3ifsfk8qe">
              <ShieldAlertIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-1778" data-mp="2hko8xqf6sd31" />
              Travelling with this request
            </h2>
            <ul className="mt-3 space-y-2" data-id="element-1779" data-mp="2rc2ae3qp7myt">
              {customer.dietary.allergies.map(allergy => <li key={allergy.id} className="flex items-start justify-between gap-3" data-id="element-1780" data-mp="2md90elamee3e">
                  <span className="text-sm text-ink" data-id="element-1781" data-mp="30cqiyixbov4b">{allergy.allergen}</span>
                  <Badge tone={severityTone[allergy.severity]} data-id="element-1782" data-mp="30anu427wp816">{severityLabel[allergy.severity]}</Badge>
                </li>)}
            </ul>
            <p className="mt-3 text-[13px] text-ink-2" data-id="element-1783" data-mp="3f0ih5ev52f16">{allergySummary()}</p>
            <Link to="/app/account/dietary" className="mt-3 inline-block text-[13px] font-medium text-primary underline underline-offset-2" data-id="element-1784" data-mp="3jz23vhhk3uul">
              Edit dietary profile
            </Link>
          </Card>

          {!custom && fulfillment === 'in_home' && <Card className="p-5" data-id="element-1785" data-mp="2w8pltdslkhqa">
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1786" data-mp="2w7efp7boixw0">Your kitchen vs this menu</h2>
              {gaps.length === 0 ? <p className="mt-3 flex items-start gap-2 text-sm text-success" data-id="element-1787" data-mp="2n92jmps1n7w6">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1788" data-mp="2r9lbukxumfyw" />
                  Your kitchen has everything this menu needs.
                </p> : <>
                  <p className="mt-3 flex items-start gap-2 text-sm text-ink-2" data-id="element-1789" data-mp="2i9pws516a0tl">
                    <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-1790" data-mp="2xr7tctgspsgq" />
                    This menu wants {gaps.map(id => equipmentLabel(id).toLowerCase()).join(', ')}, which your kitchen profile
                    does not list. The chef sees this and can bring their own.
                  </p>
                  <Link to="/app/account/kitchen" className="mt-3 inline-block text-[13px] font-medium text-primary underline underline-offset-2" data-id="element-1791" data-mp="2jqdhu1ygbbdu">
                    Update kitchen profile
                  </Link>
                </>}
            </Card>}
        </aside>
      </form>
    </AppShell>;
}